import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DayCard = ({ day, isLast, onAddActivity, onEditActivity, onDeleteActivity, onEditAccommodation }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      transport: 'Car',
      food: 'Utensils',
      activity: 'Waves',
      wellness: 'Heart',
      sightseeing: 'Camera',
      shopping: 'ShoppingBag',
      entertainment: 'Music'
    };
    return icons?.[category] || 'MapPin';
  };

  const getCategoryColor = (category) => {
    const colors = {
      transport: 'text-blue-600',
      food: 'text-orange-600',
      activity: 'text-teal-600',
      wellness: 'text-pink-600',
      sightseeing: 'text-purple-600',
      shopping: 'text-green-600',
      entertainment: 'text-yellow-600'
    };
    return colors?.[category] || 'text-gray-600';
  };

  return (
    <div className="relative">
      {/* Timeline Connector */}
      {!isLast && (
        <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-border" />
      )}

      <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all duration-300">
        {/* Day Header */}
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 md:p-5 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg z-10">
                {day?.day}
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-foreground">{day?.title}</h3>
                <p className="text-sm text-muted-foreground">{formatDate(day?.date)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="Plus"
              onClick={() => onAddActivity(day?.day)}
            >
              Add Activity
            </Button>
          </div>
        </div>

        {/* Activities */}
        <div className="p-4 md:p-5 space-y-3">
          {day?.activities?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Calendar" size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No activities planned yet</p>
            </div>
          ) : (
            day?.activities?.map((activity) => (
              <div
                key={activity?.id}
                className="bg-background rounded-lg border border-border p-4 hover:border-primary/50 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg bg-background border border-border flex items-center justify-center ${getCategoryColor(activity?.category)}`}>
                      <Icon name={getCategoryIcon(activity?.category)} size={18} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                          {activity?.time}
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {activity?.category}
                        </span>
                      </div>
                      <h4 className="font-semibold text-foreground mb-1">{activity?.title}</h4>
                      <p className="text-sm text-muted-foreground">{activity?.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Icon name="Coins" size={14} className="text-accent" />
                        <span className="text-sm font-semibold text-accent">
                          ₱{activity?.cost?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onEditActivity(day?.day, activity)}
                      iconName="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => onDeleteActivity(day?.day, activity?.id)}
                      iconName="Trash2"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Accommodation */}
        {day?.accommodation && (
          <div className="border-t border-border p-4 md:p-5 bg-secondary/5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <Icon name="Hotel" size={18} className="text-secondary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-secondary">Accommodation</span>
                  </div>
                  <h4 className="font-semibold text-foreground mb-1">{day?.accommodation?.name}</h4>
                  <p className="text-sm text-muted-foreground mb-2">{day?.accommodation?.address}</p>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>Check-in: {day?.accommodation?.checkIn}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>Check-out: {day?.accommodation?.checkOut}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Coins" size={12} className="text-accent" />
                      <span className="font-semibold text-accent">₱{day?.accommodation?.cost?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEditAccommodation(day?.day, day?.accommodation)}
                iconName="Edit"
              />
            </div>
          </div>
        )}

        {!day?.accommodation && (
          <div className="border-t border-border p-4 md:p-5 bg-secondary/5">
            <button
              onClick={() => onEditAccommodation(day?.day, null)}
              className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="Plus" size={16} />
              Add Accommodation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayCard;